import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsTelephone, BsBuilding, BsPerson, BsChevronDown } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Menu from '#globalComponents/Overlay/Menu';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';
import { getGermanLabel } from '#lib/constants/contactRoles';

/**
 * Dropdown button that lets the user pick which phone number to dial
 * (company number or any contact number). If only one number is available,
 * it dials directly without showing the dropdown.
 */
const AircallDialDropdown = ({ company, contacts, dialLoading, onDial }) => {
	const [open, setOpen] = useState(false);

	const numbers = [];
	if (company?.phoneNumber) {
		numbers.push({ label: company.companyName || 'Firma', phone: company.phoneNumber, type: 'company' });
	}
	for (const c of contacts ?? []) {
		if (c.phoneNumber) {
			const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Kontakt';
			numbers.push({ label: name, phone: c.phoneNumber, type: 'contact', role: c.companyRole });
		}
	}

	const handleClick = () => {
		if (numbers.length === 0) return;
		if (numbers.length === 1) {
			onDial(numbers[0].phone);
			return;
		}
		setOpen((prev) => !prev);
	};

	const handleSelect = (phone) => {
		setOpen(false);
		onDial(phone);
	};

	if (numbers.length === 0) return null;

	const trigger = (
		<Button onClick={handleClick} variant="outline" size="sm" loading={dialLoading}>
			<BsTelephone className="w-3 h-3" />
			{dialLoading ? 'Verbinde…' : 'Aircall'}
			{numbers.length > 1 && <BsChevronDown className="w-2.5 h-2.5 ml-0.5" />}
		</Button>
	);

	return (
		<Popover open={open} onClose={() => setOpen(false)} trigger={trigger} align="end">
			<Panel.Body padded={false}>
				<Menu>
					<Menu.Label>Nummer wählen</Menu.Label>
					{numbers.map((n, i) => (
						<Menu.Item
							key={i}
							onClick={() => handleSelect(n.phone)}
							trailing={n.role ? getGermanLabel(n.role) : undefined}
						>
							<span className="flex items-center gap-2.5">
								<span
									className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
										n.type === 'company'
											? 'bg-primary-200 text-primary-700'
											: 'bg-bgBase-400 text-bgBase-800'
									}`}
								>
									{n.type === 'company' ? (
										<BsBuilding className="w-3 h-3" />
									) : (
										<BsPerson className="w-3.5 h-3.5" />
									)}
								</span>
								<span className="min-w-0 flex-1">
									<span className="block text-xs font-medium text-text truncate">{n.label}</span>
									<span className="block text-[10px] text-bgBase-800 font-mono">{n.phone}</span>
								</span>
							</span>
						</Menu.Item>
					))}
				</Menu>
			</Panel.Body>
		</Popover>
	);
};

AircallDialDropdown.propTypes = {
	company: PropTypes.object,
	contacts: PropTypes.array,
	dialLoading: PropTypes.bool,
	onDial: PropTypes.func.isRequired,
};

export default AircallDialDropdown;
